import { useEffect, useState } from 'react';
import './greenIdForm.scss';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAppContext } from 'hooks/useAppContext';
import { auSetGreenIdConfig } from './auGreenIdConfig';
import { nzSetGreenIdConfig } from './nzGreenIdConfig';
import { GREEN_ID_TOKEN_EXPIRY_TIME, LONG_STALE_TIME } from 'constant';
import { useHistory } from 'react-router-dom';
import { Routes } from 'types';

const greenIdScripts = [
  `${process.env.REACT_APP_GREEN_ID_BASE_URL}/libs/jquery-3.5.1.min.js`,
  `${process.env.REACT_APP_GREEN_ID_BASE_URL}/greenidui.min.js`,
];

const GREEN_ID_FORM_INSERT_ELEMENT_ID = 'greenid-div';

/**
 * Overwrites specific text content in the GreenID form after it's rendered
 * Continuously monitors and updates content every 300ms to handle SDK rerenders
 *
 * We have to do it in this hacky way because GreenID web SDK does not support these content customisation from
 * `greenIdConfig.js` file.
 */
const overwriteGreenIdContent = () => {
  const performOverwrite = () => {
    // Replace "Or you can:"
    const orYouCanHeading = document.querySelector('#greenid-div .dropdown-menu .pull-left:nth-child(2) h2');
    if (orYouCanHeading && orYouCanHeading.textContent?.includes('Or you can:')) {
      orYouCanHeading.textContent = 'Alternatively, you can';
    }

    // Replace "Save & complete later"
    const saveCompleteLink = document.querySelector('#greenid-div a[data-target="#greenid-completelater"]');
    if (saveCompleteLink && saveCompleteLink.textContent?.includes('Save & complete later')) {
      saveCompleteLink.textContent = 'Manually upload your documentation';
    }
  };

  const intervalId = setInterval(performOverwrite, 300);
  return intervalId;
};

type UseGreenIdProps = {
  successRedirectPath: string;
  cancelRedirectPath: string;
  lockedOutRedirectPath?: string | null;
  countryCode: 'AU' | 'NZ';
  useCase: 'ach' | 'aml-nz';
  dev?: boolean;
};

function resolveGreenIdCredentials(useCase: 'ach' | 'aml-nz') {
  if (useCase === 'aml-nz') {
    return {
      account: process.env.REACT_APP_AML_GREEN_ID_ACCOUNT,
      apiCode: process.env.REACT_APP_AML_GREEN_ID_API_CODE,
    };
  }
  return {
    account: process.env.REACT_APP_ACH_GREEN_ID_ACCOUNT,
    apiCode: process.env.REACT_APP_ACH_GREEN_ID_API_CODE,
  };
}

/**
 * @see https://vixverify.atlassian.net/wiki/spaces/GREEN/pages/11206904/Reference+Tables#ReferenceTables-OverallOutcomeStates
 */
type VerificationStatus =
  | 'VERIFIED'
  | 'VERIFIED_ADMIN'
  | 'VERIFIED_WITH_CHANGES'
  | 'IN_PROGRESS'
  | 'PENDING'
  | 'LOCKED_OUT';

export const ApiAchConsentEndpointError = 'ApiAchConsentEndpointError';

/**
 * Dynamically adds CSS to hide the GreenID cancel button.
 * For now, we are always hiding it.
 */
const hideCancelButton = () => {
  // Check if the style has already been added to avoid duplicates
  const existingStyle = document.getElementById('greenid-dynamic-styles');
  if (existingStyle) return;

  const style = document.createElement('style');
  style.id = 'greenid-dynamic-styles';
  style.type = 'text/css';

  const css = `
    #greenid-div .dropdown-menu .pull-left:nth-child(2) {
      display: none !important;
    }
  `;

  style.appendChild(document.createTextNode(css));
  document.head.appendChild(style);
};

export function useGreenId({
  successRedirectPath,
  cancelRedirectPath,
  lockedOutRedirectPath,
  countryCode,
  useCase,
  dev = false,
}: UseGreenIdProps) {
  const { account: greenIdAccount, apiCode: greenIdApiCode } = resolveGreenIdCredentials(useCase);
  const { state } = useAppContext();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [tokenExpiryTimeoutId, setTokenExpiryTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [contentOverwriteIntervalId, setContentOverwriteIntervalId] = useState<NodeJS.Timeout | null>(null);
  const { isLoading: isFetchingVerificationToken } = useQuery({
    queryKey: ['verificationToken', { appNum: state.appNum }],
    queryFn: async () => {
      try {
        const endpoint = `${process.env.REACT_APP_SMP_ADAPTOR_URL}/ach/${state.appNum}/consent`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${state.accessToken}`,
          },
          body: JSON.stringify({
            consent: true,
          }),
        });
        if (!response.ok) {
          const error = new Error(
            `API error from endpoint ${endpoint} - HTTP response status: ${
              response.status
            }; response body: ${await response.text()}`,
          );
          error.name = ApiAchConsentEndpointError;
          throw error;
        }
        const data = await response.json();
        setVerificationToken(data?.token);
        return data.token;
      } catch (error) {
        console.error(error);
        setError(
          (error as any)?.name ||
            `Failed to fetch verification token: ${error instanceof Error ? error.message : error}`,
        );
      }
    },
    staleTime: LONG_STALE_TIME,
    enabled: !dev && !error && !verificationToken && !!state.appNum,
    retry: dev,
  });

  const { mutate: sendAchCompleteNotification, isLoading: isSendingAchCompleteNotification } = useMutation(
    async ({ noAdditionalId }: { noAdditionalId: boolean }) => {
      const response = await fetch(`${process.env.REACT_APP_SMP_ADAPTOR_URL}/ach/${state.appNum}/notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${state.accessToken}`,
        },
        body: JSON.stringify({
          verificationId: state.appNum,
          noAdditionalId,
        }),
      });
      const data = await response.json();
      return data.verificationToken;
    },
    {
      onError: (error) => {
        setError(`SMP Adaptor notification endpoint error - ${error instanceof Error ? error.message : error}`);
      },
    },
  );

  // Load GreenID assets (scripts and CSS)
  useEffect(() => {
    if (scriptsLoaded) return;

    setupGreenIdConfig(countryCode);

    let loadedScripts = 0;
    const totalScripts = greenIdScripts.length;
    let cssLoaded = false;

    const loadScript = (src: string) => {
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        loadedScripts++;
        checkAllAssetsLoaded();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;

      script.onload = () => {
        loadedScripts++;
        checkAllAssetsLoaded();
      };

      script.onerror = () => {
        setError(`Failed to load script: ${src}`);
      };

      document.head.appendChild(script);
    };

    const loadCSS = (href: string) => {
      const existingLink = document.querySelector(`link[href="${href}"]`);
      if (existingLink) {
        cssLoaded = true;
        checkAllAssetsLoaded();
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.media = 'screen';
      link.href = href;

      link.onload = () => {
        cssLoaded = true;
        checkAllAssetsLoaded();
      };

      link.onerror = () => {
        setError(`Failed to load CSS: ${href}`);
      };

      document.head.appendChild(link);
    };

    const checkAllAssetsLoaded = () => {
      if (loadedScripts === totalScripts && cssLoaded) {
        setScriptsLoaded(true);
        if (countryCode === 'AU') {
          hideCancelButton();
        }
      }
    };

    // Load all scripts
    greenIdScripts.forEach(loadScript);

    // Load CSS
    loadCSS(process.env.REACT_APP_GREEN_ID_CSS!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // No dependencies - run once only

  // Dev fetch verification token (only when dev is true)
  useEffect(() => {
    if (!dev || verificationToken) return;

    const fetchDevToken = async () => {
      try {
        const verificationId = await dev_createVerificationId(countryCode, greenIdAccount, greenIdApiCode);
        const token = await dev_getVerificationToken(verificationId, greenIdAccount, greenIdApiCode);
        setVerificationToken(token);
      } catch (err) {
        setError('Failed to fetch dev verification token');
      }
    };

    fetchDevToken();
  }, [dev, countryCode, verificationToken, greenIdAccount, greenIdApiCode]);

  /**
   * Redirect to SessionExpired page 29 minutes 55 seconds after obtaining verification token
   * Because GreenID has a 30 minutes token expiry time limit.
   */
  useEffect(() => {
    if (!verificationToken) {
      if (tokenExpiryTimeoutId) {
        clearTimeout(tokenExpiryTimeoutId);
        setTokenExpiryTimeoutId(null);
      }
      return;
    }

    if (!tokenExpiryTimeoutId) {
      const timeoutId = setTimeout(() => {
        history.push(Routes.sessionExpired);
      }, GREEN_ID_TOKEN_EXPIRY_TIME - 5000);

      setTokenExpiryTimeoutId(timeoutId);
    }

    // Cleanup timeout when component unmounts or token changes
    return () => {
      if (tokenExpiryTimeoutId) {
        clearTimeout(tokenExpiryTimeoutId);
      }
    };
  }, [verificationToken, history, dev, tokenExpiryTimeoutId]);

  // Render GreenID UI form
  useEffect(() => {
    if (!verificationToken || !scriptsLoaded) return;

    const initializeGreenId = () => {
      // Check if greenidJQuery and greenidUI are available
      if (typeof (window as any).greenidJQuery === 'undefined' || typeof (window as any).greenidUI === 'undefined') {
        return;
      }

      const greenidJQuery = (window as any).greenidJQuery;
      const greenidUI = (window as any).greenidUI;

      greenidJQuery(document).ready(async () => {
        if (!verificationToken) {
          setError('Verification token is required');
          return;
        }

        const registerCallback = (...result: any) => {};

        const errorCallback = (
          verificationToken?: string,
          overallState?: 'TokenTimeoutFault' | unknown,
          errorName?: 'unknown' | string,
        ) => {
          setError(
            `GreenID 'greenidUI.setup' - verificationToken: ${verificationToken}; overallState: ${overallState}; error: ${errorName}`,
          );
        };

        const sourceAttemptCallback = (...result: any) => {};

        const preSubmitValidationCallback = (...result: any): boolean => {
          return true;
        };

        const sessionCancelledCallback = async (verificationToken: string, status: VerificationStatus) => {
          await sendAchCompleteNotification({ noAdditionalId: true });
          history.push(cancelRedirectPath);
        };

        const sessionCompleteCallback = (verificationToken: string, status: VerificationStatus) => {
          if (status !== 'IN_PROGRESS') {
            sendAchCompleteNotification({ noAdditionalId: false });
          }

          if (status === 'LOCKED_OUT' && lockedOutRedirectPath) {
            history.push(lockedOutRedirectPath);
          } else if (status === 'LOCKED_OUT' && !lockedOutRedirectPath) {
            setError('LOCKED_OUT');
          } else {
            history.push(successRedirectPath);
          }
        };

        try {
          /**
           * @see https://vixverify.atlassian.net/wiki/spaces/GREEN/pages/359204260/Callbacks
           */
          await greenidUI.setup({
            environment: process.env.REACT_APP_ENV === 'prod' ? 'prod' : 'test',
            formId: 'latitude-additional-card-holder',
            frameId: GREEN_ID_FORM_INSERT_ELEMENT_ID,
            country: countryCode,
            registerCallback,
            errorCallback,
            sessionCompleteCallback,
            sourceAttemptCallback,
            sessionCancelledCallback,
            preSubmitValidationCallback,
            enableCancelButton: false,
          });

          await greenidUI.show(
            greenIdAccount,
            greenIdApiCode,
            verificationToken,
          );
          const intervalId = overwriteGreenIdContent();
          setContentOverwriteIntervalId(intervalId);
        } catch (error) {
          setError(error instanceof Error ? error.message : `GreenID web SDK error - ${error}`);
        } finally {
          setIsLoading(false);
        }
      });
    };

    const checkAndInitialize = () => {
      // Check if all required GreenID objects are available
      if (typeof (window as any).greenidJQuery !== 'undefined' && typeof (window as any).greenidUI !== 'undefined') {
        initializeGreenId();
      } else {
        // Poll every 50ms until objects are available, max 20 times (1 second total)
        let attempts = 0;
        const maxAttempts = 20;

        const pollForGreenId = () => {
          attempts++;
          if (
            typeof (window as any).greenidJQuery !== 'undefined' &&
            typeof (window as any).greenidUI !== 'undefined'
          ) {
            initializeGreenId();
          } else if (attempts < maxAttempts) {
            setTimeout(pollForGreenId, 50);
          } else {
            setError('GreenID objects not available after maximum polling attempts');
          }
        };

        pollForGreenId();
      }
    };

    checkAndInitialize();
  }, [
    verificationToken,
    scriptsLoaded,
    countryCode,
    successRedirectPath,
    cancelRedirectPath,
    dev,
    sendAchCompleteNotification,
    history,
    state.countryCode,
    lockedOutRedirectPath,
    greenIdAccount,
    greenIdApiCode,
  ]); // Run when token is set, scripts are loaded, or dependencies change

  // Cleanup contentOverwriteInterval when component unmounts
  useEffect(() => {
    return () => {
      if (contentOverwriteIntervalId) {
        clearInterval(contentOverwriteIntervalId);
      }
    };
  }, [contentOverwriteIntervalId]);

  return {
    isLoading: isLoading || (!dev && isFetchingVerificationToken) || isSendingAchCompleteNotification,
    error,
    elementId: GREEN_ID_FORM_INSERT_ELEMENT_ID,
  };
}

/**
 * **DEVELOPMENT/TESTING ONLY - DISABLED IN PRODUCTION**
 *
 * Creates a verification ID for development and testing purposes by registering
 * a mock user with hardcoded test data against the GreenID test environment.
 *
 * This function bypasses the normal production flow and creates a verification
 * session with predefined test user information including name, address, and
 * contact details.
 *
 * @param countryCode - The country code ('AU' or 'NZ') for which to create the verification
 * @returns Promise that resolves to the verification ID string
 *
 * @warning This function should NEVER be called in production environments.
 * It uses hardcoded test data and connects to test endpoints only.
 */
async function dev_createVerificationId(countryCode: 'AU' | 'NZ', accountId?: string, apiCode?: string) {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  const response = await fetch('https://simpleui-test-au.vixverify.com/df/register', {
    method: 'POST',
    headers: headers,
    body: JSON.stringify({
      accountId,
      apiCode,
      country: countryCode,
      ruleId: countryCode,
      givenNames: 'John',
      middleNames: '',
      surname: 'Smith',
      postcode: '1010',
      streetName: 'Tangihua',
      streetNumber: '8',
      streetType: 'Street',
      suburb: 'Auckland',
      townCity: 'Auckland',
      dob: '1/4/1987',
      mobilePhone: '+61412345678',
      email: 'test@email.com',
    }),
  });
  const result = await response.text();
  return JSON.parse(result).verificationId;
}

/**
 * **DEVELOPMENT/TESTING ONLY - DISABLED IN PRODUCTION**
 *
 * Retrieves a verification token for an existing verification ID in the
 * development/testing environment. This token is used to initialize the
 * GreenID UI form for identity verification.
 *
 * This function connects to the GreenID test environment and exchanges
 * a verification ID for a verification token that can be used to start
 * the identity verification process.
 *
 * @param verificationId - The verification ID obtained from dev_createVerificationId
 * @returns Promise that resolves to the verification token string
 *
 * @warning This function should NEVER be called in production environments.
 * It connects to test endpoints and should only be used for development purposes.
 */
async function dev_getVerificationToken(verificationId: string, accountId?: string, apiCode?: string) {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');

  const raw = JSON.stringify({
    accountId,
    apiCode,
    verificationId: verificationId,
  });

  const response = await fetch('https://simpleui-test-au.vixverify.com/df/getVerificationToken', {
    method: 'POST',
    headers,
    body: raw,
    redirect: 'follow',
  });
  const result = await response.text();
  return JSON.parse(result).token;
}

const setupGreenIdConfig = (countryCode: 'AU' | 'NZ') => {
  if (!(window as any).greenidConfigLoaded) {
    (window as any).greenidConfigLoaded = true;

    var greenidConfig = countryCode === 'AU' ? auSetGreenIdConfig() : nzSetGreenIdConfig();
    (window as any).greenidConfig = greenidConfig;
  }
};
